import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Tooltip, Select, Spin } from 'antd';
import {
    CheckCircleOutlined,
    FormOutlined,
    AuditOutlined,
    FileProtectOutlined,
    ShoppingCartOutlined,
    SafetyCertificateOutlined,
    FileDoneOutlined,
    RocketOutlined,
    CarOutlined,
    DollarCircleOutlined,
} from '@ant-design/icons';
import { colors } from '../../utils/Utils';
import * as Icons from '@ant-design/icons';
import usersApis from '../../api/master/users';
import purchaseOrderCreation from '../../api/purchase-orders/pruchaseOrderCreation';
import mainDashboardApis from '../../api/dashboard/MainDashboard';
import dayjs from 'dayjs';

const { Option } = Select;

const renderIcon = (iconName: string) => {
    // Log the raw iconName for debugging
  
    // Clean the icon name string by removing '<', '/', and '>'
    const cleanedIconName = iconName.replace(/[<>/]/g, '').trim();
  
    // Log the cleaned iconName for debugging
  
    // Ensure cleanedIconName is treated as a string key in Icons
    const IconComponent:any = Icons[cleanedIconName as keyof typeof Icons];
  
    // Log the IconComponent for debugging
  
    // Return the icon component if it exists, otherwise return null
    return IconComponent ? <IconComponent /> : null;
  };

const P2PWidget: React.FC = (props:any) => {
    const [timelineData, setTimelineData] = useState<any[]>([]);
    const [siteFilter, setSiteFilter] = useState<string[]>([]);
    const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
    const [poFilter, setPoFilter] = useState<string[]>([]);
    const [siteOptions, setSiteOptions] = useState<any[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
    const [poOptions, setPoOptions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const hasInitializedRef = useRef(false);

    // Fetch sites - only once on mount
    useEffect(() => {
        const fetchSites = async () => {
            try {
                const data = await usersApis.getCurrentUser();
                if (data) {
                    let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
                        label: item.siteName,
                        value: item.siteID.toString(),
                    }));
                    setSiteOptions(sitesOptions || []);
                }
            } catch (error) {
                console.log(error);
            }
        };
        fetchSites();
    }, []);

    // Fetch suppliers based on site filter
    useEffect(() => {
        // Skip if siteFilter is being cleared
        if (!siteFilter || siteFilter.length === 0) {
            setSupplierOptions([]);
            return;
        }

        let isCancelled = false;
        const fetchSuppliers = async () => {
            try {
                const parentDateRange = props.dateRange || { from: '', to: '' };
                const supplierMap = new Map<string, any>();

                // Convert siteFilter array to comma-separated string
                const siteIdString = siteFilter.join(',');
                
                const formData = {
                    siteId: siteIdString,
                    startDate: parentDateRange.from || dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
                    endDate: parentDateRange.to || dayjs().format('YYYY-MM-DD'),
                };
                
                const res: any = await mainDashboardApis.purchaseToPaySupplierLsitBySiteId(formData);
                
                // Don't update state if component unmounted or filter changed
                if (isCancelled) return;
                
                const { response, status } = res;
                if (status && response?.data) {
                    // Use Map to avoid duplicates
                    response.data.forEach((item: any) => {
                        const supplierId = item?.supplierId?.toString();
                        if (!supplierMap.has(supplierId)) {
                            supplierMap.set(supplierId, {
                                label: item?.supplierName,
                                value: supplierId,
                            });
                        }
                    });
                }

                if (!isCancelled) {
                    setSupplierOptions(Array.from(supplierMap.values()));
                }
            } catch (error) {
                if (!isCancelled) {
                    console.log(error);
                    setSupplierOptions([]);
                }
            }
        };
        fetchSuppliers();

        return () => {
            isCancelled = true;
        };
    }, [siteFilter, props.dateRange]);

    // Fetch purchase orders based on site and supplier filters
    useEffect(() => {
        // Skip if both filters are not selected
        if (!siteFilter || siteFilter.length === 0 || !supplierFilter || supplierFilter.length === 0) {
            setPoOptions([]);
            return;
        }

        let isCancelled = false;
        const fetchPurchaseOrders = async () => {
            try {
                const poMap = new Map<string, any>();

                // Convert arrays to comma-separated strings
                const siteIdString = siteFilter.join(',');
                const supplierIdString = supplierFilter.join(',');
                
                const res = await mainDashboardApis.purchaseToPayOrderListbysiteAndSupplier({
                    siteId: siteIdString,
                    supplierId: supplierIdString,
                });
                
                // Don't update state if component unmounted or filters changed
                if (isCancelled) return;
                
                const { response, status } = res;
                if (status && response) {
                    // Handle both response.data (nested) or response (direct array)
                    const dataArray = response.data || response;
                    if (Array.isArray(dataArray)) {
                        dataArray.forEach((item: any) => {
                            const orderId = item.orderId;
                            const orderNumber = item.orderNumber;
                            // Use orderId as value for API calls, but display orderNumber as label
                            if (orderId && !poMap.has(orderId)) {
                                poMap.set(orderId, {
                                    label: orderNumber || orderId,
                                    value: orderId,
                                });
                            }
                        });
                    }
                }

                if (!isCancelled) {
                    setPoOptions(Array.from(poMap.values()));
                }
            } catch (error) {
                if (!isCancelled) {
                    console.log(error);
                    setPoOptions([]);
                }
            }
        };
        fetchPurchaseOrders();

        return () => {
            isCancelled = true;
        };
    }, [siteFilter, supplierFilter]);

    // Combined effect to handle timeline data - either from props or API
    useEffect(() => {
        const hasLocalFilters = (siteFilter && siteFilter.length > 0) || (supplierFilter && supplierFilter.length > 0) || (poFilter && poFilter.length > 0);
        let isCancelled = false;
        
        // If local filters are applied, fetch from API
        if (hasLocalFilters) {
            const fetchP2PCycleData = async () => {
                setIsLoading(true);
                try {
                    const parentSite = props.site || '';
                    const parentContractor = props.contractor || '';
                    const parentDateRange = props.dateRange || { from: '', to: '' };
                    
                    // Determine which filters to use - local filters take precedence if set
                    // Convert arrays to comma-separated strings for API
                    const formData: any = {
                        siteId: (siteFilter && siteFilter.length > 0) ? siteFilter.join(',') : (parentSite || ''),
                        contractorId: parentContractor || '',
                        supplierId: (supplierFilter && supplierFilter.length > 0) ? supplierFilter.join(',') : '',
                        orderId: (poFilter && poFilter.length > 0) ? poFilter.join(',') : '',
                        startDate: parentDateRange.from || dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
                        endDate: parentDateRange.to || dayjs().format('YYYY-MM-DD'),
                    };
                    
                    const res = await mainDashboardApis.procureToPayCycle(formData);
                    
                    // Don't update state if component unmounted or filters changed
                    if (isCancelled) {
                        setIsLoading(false);
                        return;
                    }
                    
                    const { response, status } = res;
                    if (status) {
                        setTimelineData(response || []);
                    }
                } catch (error) {
                    if (!isCancelled) {
                        console.log(error);
                    }
                } finally {
                    if (!isCancelled) {
                        setIsLoading(false);
                    }
                }
            };
            fetchP2PCycleData();
            
            return () => {
                isCancelled = true;
            };
        } 
        // If no local filters and prop data exists, use prop data
        else if (props.procureToPayCycleData) {
            setTimelineData(props.procureToPayCycleData);
        }
        // Initialize on mount if prop data exists
        else if (!hasInitializedRef.current && props.procureToPayCycleData) {
            setTimelineData(props.procureToPayCycleData);
            hasInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteFilter, supplierFilter, poFilter]);

    return (
        <div>
            <div
                className="card-title-group mb-1"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '8px',
                }}
            >
                <div className="card-title">
                    <h6 className="title">Procure to Pay Cycle</h6>
                    <p>Tracking the end-to-end procurement process</p>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        minWidth: 0,
                        paddingBottom: '4px',
                    }}
                >
                    <Select
                        mode="multiple"
                        placeholder="Filter by Site"
                        style={{ width: 170, flexShrink: 0 }}
                        value={siteFilter}
                        onChange={(value) => {
                            setSiteFilter(value || []);
                            // Clear supplier and PO filters when site changes
                            setSupplierFilter([]);
                            setPoFilter([]);
                        }}
                        allowClear
                        maxTagCount="responsive"
                    >
                        {siteOptions?.map((item: any) => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        mode="multiple"
                        placeholder="Filter by Supplier"
                        style={{ width: 170, flexShrink: 0 }}
                        value={supplierFilter}
                        onChange={(value) => {
                            setSupplierFilter(value || []);
                            // Clear PO filter when supplier changes
                            setPoFilter([]);
                        }}
                        allowClear
                        disabled={!siteFilter || siteFilter.length === 0}
                        maxTagCount="responsive"
                    >
                        {supplierOptions?.map((item: any) => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        mode="multiple"
                        placeholder="Filter by PO#"
                        style={{ width: 170, flexShrink: 0 }}
                        value={poFilter}
                        onChange={(value) => setPoFilter(value || [])}
                        allowClear
                        showSearch
                        disabled={!siteFilter || siteFilter.length === 0 || !supplierFilter || supplierFilter.length === 0}
                        filterOption={(input, option: any) =>
                            option?.children?.toLowerCase()?.includes(input?.toLowerCase())
                        }
                        maxTagCount="responsive"
                    >
                        {poOptions?.map((item: any) => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div style={{ position: 'relative' }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {timelineData?.map((item:any, index:any) => (
                        <React.Fragment key={index}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    textAlign: 'center',
                                    width: 100,
                                    height: '100px',
                                }}
                            >
                                <Avatar
                                    style={{
                                        backgroundColor: colors[index],
                                        color: '#fff',
                                        marginBottom: '8px',
                                        width: 50,
                                        height: 50,
                                    }}
                                    icon={renderIcon(item.icon)}
                                />
                                <div>{item.title}</div>
                            </div>
                            {index < timelineData?.length - 1 && (
                                <div style={{ flex: 1, position: 'relative', textAlign: 'center' }}>
                                    {/* avgDays positioned above the line */}
                                    <Tooltip title={item.avgDays}>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '-24px',
                                                width: '100%',
                                                fontSize: '12px',
                                                color: '#888',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '80px', // Adjust max width as needed
                                                textAlign: 'center',
                                            }}
                                        >
                                            {item.avgDays}
                                        </div>
                                    </Tooltip>
                                    {/* Line in the middle */}
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#E5E9F2',
                                            position: 'absolute',
                                            top: '50%', // Center the line between Avatars
                                        }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default P2PWidget;
